import type * as THREE from 'three'
import type { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { BRepRegistrar } from '../../worker'
import type { FileInfo } from '../../imported-model/loaders/file-info'
import type {
  AssemblyManifest,
  ComposedAssemblyResult,
  ImportedModelResult,
  ImportedTopologySource,
  Matrix4Tuple,
} from '../../imported-model/loaders/types'
import { activateImportedModelTopologyHosts } from '../../imported-model/topology/imported-model-topology-runtime'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import { buildPuppyImportedTopologySource } from './buildPuppyImportedTopologySource'
import { buildPuppyAssemblyHierarchy } from './buildPuppyAssemblyHierarchy'
import {
  createPuppyRuntimeIndexesFromMetadata,
  createPuppyScopedAssemblyResult,
} from './createPuppyScopedAssemblyResult'
import { normalizePuppyAssemblyRigidHierarchy } from './normalizePuppyAssemblyRigidHierarchy'
import { readPuppyDatumExtensionFromAssemblyExtensions } from './puppyDatumExtension'
//------ 草图离线包定义持久化 开始------
import { readPuppySketchExtensionFromAssemblyExtensions } from './puppySketchExtension'
import {
  readPuppySketchEntityReferenceExtensionFromAssemblyExtensions,
} from './puppySketchEntityReferenceExtension'
//------ 草图离线包定义持久化 结束------
//------ 特征离线包定义持久化 开始------
import { readPuppyFeatureExtensionFromAssemblyExtensions } from './puppyFeatureExtension'
//------ 特征离线包定义持久化 结束------
import { readPuppySectionViewExtensionFromAssemblyExtensions } from './puppySectionViewExtension'
//------ 线性零部件阵列离线包定义持久化 开始------
import {
  materializePuppyLinearComponentPatternDocument,
  readPuppyLinearComponentPatternExtensionFromAssemblyExtensions,
} from './puppyLinearComponentPatternExtension'
//------ 线性零部件阵列离线包定义持久化 结束------
//------ 装配体引用复制导入 开始------
import { materializePuppyAssemblyReferenceCopies } from './materializePuppyAssemblyReferenceCopies'
import { AssemblyReferenceCopyKind } from '../../assembly/reference-copy'
//------ 装配体引用复制导入 结束------
import { parsePuppyPackage } from './parsePuppyPackage'
import { createPuppyBodyTopologyAssetKey } from './bodyTopologyAssetKey'
import { PuppyDisplayInstancedMeshMaterializer } from './PuppyDisplayInstancedMeshMaterializer'
import { PuppyBrepBodySelectionResolver } from './PuppyBrepBodySelectionResolver'
import type {
  ImportPuppyPackageOptions,
  ImportPuppyPackageResult,
  PuppyAssemblyDocument,
  PuppyAssemblyNode,
  PuppyBrepIndexEntry,
  PuppyDisplayNodeBodyBinding,
  PuppyImportedModelTabTemplate,
  PuppyImportTiming,
  PuppyPackageDocumentType,
  PuppyPackageParseResult,
  PuppyParsedTopologyRuntimeAsset,
} from './types'

/**
 * 读取当前高精度时间。
 *
 * @returns 毫秒时间戳。
 */
function readNowMs(): number {
  return globalThis.performance?.now?.() ?? Date.now()
}

/**
 * 计算耗时。
 *
 * @param startMs 开始时间。
 * @returns 毫秒耗时。
 */
function elapsedMs(startMs: number): number {
  return Math.round(readNowMs() - startMs)
}

/**
 * 解析导入显示根名称。
 *
 * @param input 名称输入。
 * @returns 显示根名称。
 */
function resolveDisplayRootName(input: {
  projectName: string
  packageName?: string
}): string {
  const projectName = input.projectName.trim()
  if (projectName.length > 0) {
    return projectName
  }

  const packageName = input.packageName?.replace(/\.puppy$/i, '').trim() ?? ''
  return packageName.length > 0 ? packageName : 'PuppyCAD Offline Package'
}

/**
 * 判断 GLB 节点是否已经携带 PuppyCAD 业务装配身份。
 *
 * @param object GLTFLoader 创建的场景对象。
 * @returns 是否为 display/assembly 业务节点。
 */
function hasPuppyDisplayAssemblyIdentity(object: THREE.Object3D): boolean {
  const puppy = object.userData?.puppy as { assemblyNodeId?: unknown } | undefined
  return typeof puppy?.assemblyNodeId === 'string' && puppy.assemblyNodeId.trim().length > 0
}

/**
 * 标记 GLTFLoader 解析产生的非业务容器链。
 *
 * GLTFExporter 会把传入的临时导出根也写成 glTF node。导入后形成
 * `gltf.scene -> document root -> business node`，其中前两层都不代表项目 occurrence。
 * 这里只沿“无 Puppy 身份、单子节点、非 Mesh”的链向下标记，遇到首个业务节点立即停止。
 *
 * @param scene GLTFLoader.scene 容器。
 */
function markPuppyDisplayRuntimeWrapperChain(scene: THREE.Object3D): void {
  let current: THREE.Object3D | null = scene
  while (current) {
    ImportedModelObjectMetadataService.writeMetadata(current, {
      runtimeWrapper: true,
    })
    if (current.children.length !== 1) {
      return
    }
    const child = current.children[0] ?? null
    if (
      !child
      || (child as THREE.Object3D & { isMesh?: boolean }).isMesh === true
      || hasPuppyDisplayAssemblyIdentity(child)
    ) {
      return
    }
    current = child
  }
}

/**
 * 创建 `.puppy` 导入文件描述。
 *
 * @param input 创建输入。
 * @returns imported-model 文件描述。
 */
function createPuppyFileInfo(input: {
  packageId: string
  packageName?: string
  byteLength: number
}): FileInfo {
  const packageName = input.packageName?.trim()
  return {
    fileId: packageName && packageName.length > 0
      ? `puppy:${packageName}`
      : `puppy:${input.packageId}`,
    kind: '.puppy',
    uuid: input.packageId,
    size: BigInt(input.byteLength),
  }
}

/**
 * 解析 GLTFLoader。
 *
 * @param loader 外部可选 loader。
 * @returns 可用 GLTFLoader。
 */
async function resolveGltfLoader(loader?: GLTFLoader): Promise<GLTFLoader> {
  if (loader) {
    return loader
  }

  const module = await import('three/examples/jsm/loaders/GLTFLoader.js')
  return new module.GLTFLoader()
}

/**
 * 懒加载 GLB 解析运行时。
 *
 * 设计说明：
 * 1. cad-core 根入口会重新导出 `.puppy` 能力，静态依赖 GLB loader 会让 STEP 路径预加载 GLTFLoader；
 * 2. `.puppy` 只有在真实导入时才需要解析 display.glb，因此运行时模块必须延迟到导入流程内部加载。
 *
 * @returns GLB 解析与层级收集模块。
 */
async function resolveGlbRuntime(): Promise<typeof import('../../imported-model/loaders/glb')> {
  return import('../../imported-model/loaders/glb')
}

/**
 * 建立 BRep 几何对象到 display body 绑定的索引。
 *
 * @param parsed `.puppy` 解析结果。
 * @returns body 绑定索引。
 */
function createBodyBindingByGeometryObjectId(
  parsed: PuppyPackageParseResult,
): Map<string, PuppyDisplayNodeBodyBinding> {
  const bindingByGeometryObjectId = new Map<string, PuppyDisplayNodeBodyBinding>()
  parsed.brepIndex.displayNodes.forEach((displayNode) => {
    displayNode.bodyBindings.forEach((binding) => {
      if (!bindingByGeometryObjectId.has(binding.geometryObjectId)) {
        bindingByGeometryObjectId.set(binding.geometryObjectId, binding)
      }
    })
  })
  return bindingByGeometryObjectId
}

/**
 * 建立 bodyBindingId 到 topology runtime 资产的索引。
 *
 * @param parsed `.puppy` 解析结果。
 * @returns topology runtime 资产索引。
 */
function createTopologyAssetsByKey(
  parsed: PuppyPackageParseResult,
): Map<string, PuppyParsedTopologyRuntimeAsset> {
  return new Map(parsed.topologyRuntime.assets.map((asset) => [
    createPuppyBodyTopologyAssetKey(asset),
    asset,
  ]))
}

/**
 * 为 BRep 索引条目构造 Worker Shape 注册载荷。
 *
 * 设计说明：
 * 1. `.puppy` 导入时交互拓扑已经来自 topology-runtime.bin，不再从 BRep 反推；
 * 2. BRep Shape-only 注册只服务后续 STEP 导出和真实建模命令；
 * 3. 这样导入耗时与现有 GLB + topo sidecar 链路保持同一数量级。
 *
 * @param input 注册输入。
 * @returns 已完成离线包索引校验的 Worker Shape 注册载荷。
 */
function createPuppyBrepShapeRegistration(input: {
  parsed: PuppyPackageParseResult
  entry: PuppyBrepIndexEntry
  bodyBinding?: PuppyDisplayNodeBodyBinding
  fileInfo: FileInfo
  /** 旧离线包中从 assembly 定义顺序恢复的 BRep Solid 零基序号。 */
  brepBodyOrdinal?: number
}): RegisterImportedBrepShapePayload {
  const brepFile = input.parsed.brepFilesBySha256[input.entry.brepSha256]
  if (!brepFile) {
    throw new Error(`PuppyCAD 离线包缺少 BRep payload: ${input.entry.brepSha256}`)
  }

  const bodyBindingId = input.bodyBinding?.bodyBindingId ?? input.entry.geometryObjectId
  const bodyId = input.bodyBinding?.bodyId ?? input.entry.geometryObjectId

  return {
    cacheKey: input.entry.brepSha256,
    payload: brepFile.payload,
    encoding: input.entry.encoding,
    registerGeometryState: {
      objectId: input.entry.geometryObjectId,
      commandId: input.entry.commandId,
      name: input.bodyBinding?.name ?? input.entry.name,
      bodyBindingId,
      bodyId,
      sourceFileId: input.fileInfo.fileId,
      sourceFileUuid: input.fileInfo.uuid,
      sourceLevel: 'body',
      brepKey: input.entry.geometryObjectId,
      brepSha256: input.entry.brepSha256,
      brepBodyOrdinal: input.brepBodyOrdinal,
      exportDefinitionId: input.entry.geometryObjectId,
    },
  }
}

/**
 * 解析 `.puppy` 导入使用的 BRep Shape 注册能力。
 *
 * @param options `.puppy` 导入选项。
 * @returns BRep Shape 注册能力；缺少时返回 `undefined`。
 */
function resolvePuppyBrepRegistrar(
  options: ImportPuppyPackageOptions,
): BRepRegistrar | undefined {
  // brepRegistrar 是新链路的最小能力入口；workerManager 只保留为中间提交兼容别名。
  return options.brepRegistrar ?? options.workerManager
}

/**
 * 确认当前 `.puppy` 导入具备 BRep Shape 注册能力。
 *
 * @param brepRegistrar 可选 BRep Shape 注册能力。
 * @returns 已确认存在的 BRep Shape 注册能力。
 */
function requirePuppyBrepRegistrar(
  brepRegistrar: BRepRegistrar | undefined,
): BRepRegistrar & {
  registerImportedBrepShapesAtomically: NonNullable<BRepRegistrar['registerImportedBrepShapesAtomically']>
} {
  if (!brepRegistrar?.registerImportedBrepShapesAtomically) {
    throw new Error('PuppyCAD 离线包导入缺少支持原子批量注册的 brepRegistrar，无法注册 BRep Shape')
  }
  return brepRegistrar as BRepRegistrar & {
    registerImportedBrepShapesAtomically: NonNullable<BRepRegistrar['registerImportedBrepShapesAtomically']>
  }
}

/**
 * 创建导入耗时统计。
 *
 * @param input 分段耗时。
 * @returns 导入耗时统计。
 */
function createImportTiming(input: Omit<PuppyImportTiming, 'totalMs'> & {
  totalStartMs: number
}): PuppyImportTiming {
  return {
    parsePackageMs: input.parsePackageMs,
    parseDisplayGlbMs: input.parseDisplayGlbMs,
    registerBrepShapesMs: input.registerBrepShapesMs,
    buildTopologySourceMs: input.buildTopologySourceMs,
    activateRuntimeMs: input.activateRuntimeMs,
    totalMs: elapsedMs(input.totalStartMs),
  }
}

/**
 * 将 `.puppy` 装配节点语义归一化为 imported-model manifest 支持的类型。
 *
 * @param node 装配节点。
 * @param isRoot 是否为根节点。
 * @param documentType 离线包文档类型。
 * @returns imported-model 装配节点类型。
 */
function resolvePuppyAssemblyNodeSemanticKind(
  node: PuppyAssemblyNode,
  isRoot: boolean,
  documentType: PuppyPackageDocumentType,
): 'assembly' | 'part' | 'unknown' {
  if (node.semanticKind === 'assembly' || node.semanticKind === 'part') {
    return node.semanticKind
  }
  if (isRoot && documentType === 'assembly') {
    return 'assembly'
  }
  if (isRoot && documentType === 'part') {
    return 'part'
  }
  return 'unknown'
}

/**
 * 将 `.puppy` assembly.json 转成 imported-model 统一 AssemblyManifest。
 *
 * @param input 转换输入。
 * @returns imported-model 装配 manifest。
 */
function createPuppyAssemblyManifest(input: {
  assembly: PuppyAssemblyDocument
  fileInfo: FileInfo
  documentType: PuppyPackageDocumentType
}): AssemblyManifest {
  //------ 装配体引用复制导入 开始------
  const nodeById = new Map(input.assembly.nodes.map((node) => [node.nodeId, node]))
  /**
   * 引用复制扩展只保存源节点身份，局部矩阵由装配节点 transform 承载。
   * 转换为 imported-model manifest 时仍要恢复 partFileUuid 和节点 transform，
   * 保证后续再次保存或历史回放不退化为独立几何节点。
   */
  const resolveReferenceCopyPartFileUuid = (node: PuppyAssemblyNode): string | undefined => {
    const referenceCopy = node.extensions?.referenceCopy
    if (!referenceCopy || typeof referenceCopy !== 'object') {
      return undefined
    }
    const record = referenceCopy as Record<string, unknown>
    if (record.kind !== AssemblyReferenceCopyKind.ReferenceOccurrence) {
      return undefined
    }
    const explicitUuid = typeof record.sourcePartFileUuid === 'string'
      ? record.sourcePartFileUuid.trim()
      : ''
    if (explicitUuid) {
      return explicitUuid
    }
    const definitionId = typeof record.definitionId === 'string' ? record.definitionId.trim() : ''
    if (definitionId) {
      return definitionId
    }
    const sourceNodeId = typeof record.sourceNodeId === 'string' ? record.sourceNodeId.trim() : ''
    const sourceNode = sourceNodeId ? nodeById.get(sourceNodeId) : undefined
    return sourceNode?.meta?.externalFileUuid
  }
  //------ 装配体引用复制导入 结束------
  const nodesById: AssemblyManifest['nodesById'] = Object.fromEntries(input.assembly.nodes.map((node) => {
    const isRoot = node.nodeId === input.assembly.rootNodeId
    if (!node.transform) {
      throw new Error(`PuppyCAD 离线包装配节点缺少本地矩阵: ${node.nodeId}`)
    }
    const referenceCopyPartFileUuid = resolveReferenceCopyPartFileUuid(node)
    return [
      node.nodeId,
      {
        nodeId: node.nodeId,
        parentNodeId: node.parentNodeId,
        name: node.name,
        semanticKind: resolvePuppyAssemblyNodeSemanticKind(node, isRoot, input.documentType),
        //------ 装配体引用复制导入 开始------
        transform: [...node.transform] as Matrix4Tuple,
        ...(referenceCopyPartFileUuid ? { partFileUuid: referenceCopyPartFileUuid } : {}),
        //------ 装配体引用复制导入 结束------
        pmi: node.meta?.pmi,
        instanceIndex: node.meta?.instanceIndex,
        configurationName: node.meta?.configurationName,
        displayStateName: node.meta?.displayStateName,
      },
    ]
  }))

  return {
    assemblyFileId: input.fileInfo.fileId,
    assemblyFileUuid: input.fileInfo.uuid,
    version: String(input.assembly.version),
    rootNodeId: input.assembly.rootNodeId,
    nodesById,
    childIdsByNodeId: { ...input.assembly.childIdsByNodeId },
    referencedPartUuids: [],
  }
}

/**
 * 判断 `.puppy` 装配节点是否应该映射为底部模型 tab。
 *
 * 设计说明：
 * 1. root tab 代表当前导入文档，无论零件还是装配体都必须保留；
 * 2. 装配体文档的底部 tab 用于切换子装配体工作上下文，不应把普通零件全部铺开；
 * 3. 零件节点仍保留在设计树层级中，避免底部 tab 数量随实体数量失控。
 *
 * @param input tab 判定输入。
 * @returns 是否创建 tab 模板。
 */
function shouldCreatePuppyImportedModelTabTemplate(input: {
  isRoot: boolean
  documentType: PuppyPackageDocumentType
  semanticKind: 'assembly' | 'part' | 'unknown'
}): input is {
  isRoot: boolean
  documentType: PuppyPackageDocumentType
  semanticKind: 'assembly' | 'part'
} {
  if (input.isRoot) {
    return input.semanticKind === 'assembly' || input.semanticKind === 'part'
  }
  return input.documentType === 'assembly' && input.semanticKind === 'assembly'
}

/**
 * 生成 `.puppy` 虚拟 tab 模板。
 *
 * @param input tab 模板输入。
 * @returns 虚拟 tab 模板列表。
 */
function createPuppyImportedModelTabTemplates(input: {
  assembly: PuppyAssemblyDocument
  documentType: PuppyPackageDocumentType
  selectionObjectIdByAssemblyNodeId: Record<string, string>
}): PuppyImportedModelTabTemplate[] {
  return input.assembly.nodes.flatMap((node): PuppyImportedModelTabTemplate[] => {
    const isRoot = node.nodeId === input.assembly.rootNodeId
    const semanticKind = resolvePuppyAssemblyNodeSemanticKind(node, isRoot, input.documentType)
    const tabTemplateInput = {
      isRoot,
      documentType: input.documentType,
      semanticKind,
    }
    if (!shouldCreatePuppyImportedModelTabTemplate(tabTemplateInput)) {
      return []
    }
    const scopeObjectId = isRoot ? null : input.selectionObjectIdByAssemblyNodeId[node.nodeId] ?? null
    if (!isRoot && !scopeObjectId) {
      return []
    }
    return [{
      nodeId: node.nodeId,
      label: node.name,
      type: tabTemplateInput.semanticKind,
      iconType: tabTemplateInput.semanticKind,
      scopeObjectId,
      isRoot,
    }]
  })
}

/**
 * 创建 `.puppy` 装配体导入结果。
 *
 * @param input 创建输入。
 * @returns imported-model 装配体结果。
 */
function createPuppyComposedAssemblyResult(input: {
  fileInfo: FileInfo
  root: THREE.Group
  hierarchy: ComposedAssemblyResult['hierarchy']
  assembly: PuppyAssemblyDocument
  documentType: PuppyPackageDocumentType
  partName: string
  packageId: string
}): ComposedAssemblyResult {
  const { selectionIndex, sceneBindings } = createPuppyRuntimeIndexesFromMetadata(input.root)

  return {
    kind: 'composed-assembly',
    fileInfo: input.fileInfo,
    partName: input.partName,
    root: input.root,
    hierarchy: input.hierarchy,
    partAssetsByUuid: {},
    assemblyAssetsByUuid: {},
    manifest: createPuppyAssemblyManifest({
      assembly: input.assembly,
      fileInfo: input.fileInfo,
      documentType: input.documentType,
    }),
    selectionIndex,
    sceneBindings,
    warnings: [],
    sourceAssetKey: `puppy:${input.packageId}`,
  }
}

/**
 * 为 `.puppy` 底部 tab 生成真实 imported-model 结果。
 *
 * @param input 创建输入。
 * @returns assembly node ID 到模型结果的索引。
 */
function createPuppyTabModelResultsByNodeId(input: {
  modelResult: ImportedModelResult
  tabTemplates: PuppyImportedModelTabTemplate[]
  topologySource: ImportedTopologySource
}): Record<string, ImportedModelResult> {
  return Object.fromEntries(input.tabTemplates.map((template) => {
    if (input.modelResult.kind !== 'composed-assembly' || template.isRoot || !template.scopeObjectId) {
      return [template.nodeId, input.modelResult]
    }

    return [
      template.nodeId,
      createPuppyScopedAssemblyResult({
        sourceResult: input.modelResult,
        scopeObjectId: template.scopeObjectId,
        topologySource: input.topologySource,
      }),
    ]
  }))
}

/**
 * 导入 PuppyCAD 离线包。
 *
 * @param bytes `.puppy` 文件字节。
 * @param options 导入选项。
 * @returns 导入结果。
 * @throws 当离线包解析、GLB 载入、BRep 原子注册失败，或 Worker 返回的注册结果与请求不一致时抛出错误。
 */
export async function importPuppyPackage(
  bytes: Uint8Array,
  options: ImportPuppyPackageOptions,
): Promise<ImportPuppyPackageResult> {
  const totalStartMs = readNowMs()

  const parseStartMs = readNowMs()
  const parsed = await parsePuppyPackage(bytes)
  const normalizedAssemblyResult = normalizePuppyAssemblyRigidHierarchy(parsed.assembly)
  const assembly = normalizedAssemblyResult.assembly
  const datumExtension = readPuppyDatumExtensionFromAssemblyExtensions(assembly.extensions)
  //------ 线性零部件阵列离线包定义持久化 开始------
  const stableLinearComponentPatternExtension =
    readPuppyLinearComponentPatternExtensionFromAssemblyExtensions(assembly.extensions)
  //------ 线性零部件阵列离线包定义持久化 结束------
  //------ 草图离线包定义持久化 开始------
  const sketchExtension = readPuppySketchExtensionFromAssemblyExtensions(assembly.extensions)
  const sketchEntityReferenceExtension =
    readPuppySketchEntityReferenceExtensionFromAssemblyExtensions(assembly.extensions)
  //------ 草图离线包定义持久化 结束------
  //------ 特征离线包定义持久化 开始------
  const featureExtension = readPuppyFeatureExtensionFromAssemblyExtensions(assembly.extensions)
  //------ 特征离线包定义持久化 结束------
  const sectionViewExtension = readPuppySectionViewExtensionFromAssemblyExtensions(assembly.extensions)
  const parsePackageMs = elapsedMs(parseStartMs)

  const glbStartMs = readNowMs()
  const [glbRuntime, loader] = await Promise.all([
    resolveGlbRuntime(),
    resolveGltfLoader(options.gltfLoader),
  ])
  const gltf = await glbRuntime.parseGlb(loader, parsed.displayGlbBytes)
  /**
   * 离线 display 直接使用 EXT_mesh_gpu_instancing；业务拓扑与装配层仍需要 occurrence 级
   * 独立 Mesh，因此必须在任何 topology/body binding 建立前一次性物化。
   */
  new PuppyDisplayInstancedMeshMaterializer().materialize(gltf.scene)
  const parseDisplayGlbMs = elapsedMs(glbStartMs)

  const fileInfo = options.fileInfo ?? createPuppyFileInfo({
    packageId: parsed.manifest.packageId,
    packageName: options.packageName,
    byteLength: bytes.byteLength,
  })
  gltf.scene.name = resolveDisplayRootName({
    projectName: parsed.manifest.projectName,
    packageName: options.packageName,
  })
  /**
   * GLTFLoader.scene 永远是解析容器，不是 assembly.json 中的业务 occurrence。
   * 即使后续 hierarchy 构建为了统一查询把根语义镜像到该对象，导出器仍必须折叠它，
   * 否则每次“导入后再保存”都会在 display.glb 外层新增一个 Group。
   */
  markPuppyDisplayRuntimeWrapperChain(gltf.scene)

  const bodyBindingByGeometryObjectId = createBodyBindingByGeometryObjectId(parsed)
  const bodyOrdinalByGeometryObjectId = new PuppyBrepBodySelectionResolver().resolve(parsed)
  const registerStartMs = readNowMs()
  const brepRegistrar = parsed.brepIndex.entries.length > 0
    ? requirePuppyBrepRegistrar(resolvePuppyBrepRegistrar(options))
    : resolvePuppyBrepRegistrar(options)
  const brepRegistrations = parsed.brepIndex.entries.map((entry) => (
    createPuppyBrepShapeRegistration({
      parsed,
      entry,
      bodyBinding: bodyBindingByGeometryObjectId.get(entry.geometryObjectId),
      fileInfo,
      brepBodyOrdinal: bodyOrdinalByGeometryObjectId.get(entry.geometryObjectId),
    })
  ))
  // 所有离线包 BRep payload 均完成本地预校验后才进入 Worker，避免后续条目失败时留下前序 Shape。
  const registrationResults = brepRegistrations.length > 0
    ? await requirePuppyBrepRegistrar(brepRegistrar)
      .registerImportedBrepShapesAtomically(brepRegistrations)
    : []
  if (registrationResults.length !== brepRegistrations.length) {
    throw new Error(
      `PuppyCAD 离线包 BRep 原子注册结果数量不匹配: expected=${brepRegistrations.length}; actual=${registrationResults.length}`,
    )
  }
  const registeredBrepShapeEntries = registrationResults.map((registrationResult, registrationIndex) => {
    const sourceGeometryObjectId = brepRegistrations[registrationIndex]?.registerGeometryState.objectId
    if (!sourceGeometryObjectId) {
      throw new Error(
        `PuppyCAD 离线包 BRep 原子注册结果缺少来源对象 ID: index=${registrationIndex}`,
      )
    }
    /**
     * scoped-subtree 注册允许 Worker 把包内来源 ID 重定位到项目 occurrence 命名空间。
     * 这里必须保留来源 ID 与注册结果的对应关系，不能反过来用运行时 ID 查询包内索引。
     */
    return [sourceGeometryObjectId, registrationResult] as const
  })
  const registerBrepShapesMs = elapsedMs(registerStartMs)

  const topologyStartMs = readNowMs()
  const registeredBrepShapes = registeredBrepShapeEntries.map(([, result]) => result)
  const registeredShapeBySourceGeometryObjectId = new Map(registeredBrepShapeEntries)
  const topologySource = buildPuppyImportedTopologySource({
    root: gltf.scene,
    brepIndex: parsed.brepIndex,
    topologyAssetsByKey: createTopologyAssetsByKey(parsed),
    registeredShapeBySourceGeometryObjectId,
  })
  const buildTopologySourceMs = elapsedMs(topologyStartMs)

  const activateStartMs = readNowMs()
  activateImportedModelTopologyHosts(gltf.scene, topologySource)
  //------ 装配体引用复制导入 开始------
  // 普通 display、BRep 与 topology 运行时完成后再物化复制体，确保复制体只复用已导入的源对象与拓扑源。
  materializePuppyAssemblyReferenceCopies({
    root: gltf.scene,
    assembly,
    topologySource,
  })
  //------ 装配体引用复制导入 结束------
  const {
    hierarchy,
    rows,
    //------ 线性零部件阵列组件源精确物化 开始------
    objectIdByAssemblyNodeId,
    //------ 线性零部件阵列组件源精确物化 结束------
    selectionObjectIdByAssemblyNodeId,
  } = buildPuppyAssemblyHierarchy({
    root: gltf.scene,
    assembly,
    fileInfo,
    nodeIdAliasByNodeId: normalizedAssemblyResult.nodeIdAliasByNodeId,
  })

  const tabTemplates = createPuppyImportedModelTabTemplates({
    assembly,
    documentType: parsed.manifest.documentType,
    selectionObjectIdByAssemblyNodeId,
  })
  //------ 线性零部件阵列离线包定义持久化 开始------
  // 基础装配层级建立后，把稳定装配节点 ID 转回本次会话可由 Viewer 查找的 Object3D ID。
  const linearComponentPatternExtension = stableLinearComponentPatternExtension
    ? materializePuppyLinearComponentPatternDocument(
      stableLinearComponentPatternExtension,
      {
        documentRootAssemblyNodeId: assembly.rootNodeId,
        documentRootObjectId: objectIdByAssemblyNodeId[assembly.rootNodeId] ?? null,
        // 组件源必须命中真实业务对象，不能使用可能上提到父装配的 UI selection proxy。
        resolveComponentObjectId: (stableObjectId) => (
          objectIdByAssemblyNodeId[stableObjectId] ?? null
        ),
        // 方向等普通 SceneObject 引用仍可使用设计树与交互层约定的选择代理。
        resolveReferenceObjectId: (stableObjectId) => (
          selectionObjectIdByAssemblyNodeId[stableObjectId] ?? null
        ),
      },
    )
    : undefined
  //------ 线性零部件阵列离线包定义持久化 结束------
  const modelResult = parsed.manifest.documentType === 'assembly'
    ? createPuppyComposedAssemblyResult({
      fileInfo,
      root: gltf.scene,
      hierarchy,
      assembly,
      documentType: parsed.manifest.documentType,
      partName: gltf.scene.name,
      packageId: parsed.manifest.packageId,
    })
    : {
      kind: 'legacy-glb' as const,
      fileInfo,
      partName: gltf.scene.name,
      object: gltf.scene,
      gltf,
      hierarchy,
      hierarchyRows: rows,
    }
  if (modelResult.kind === 'composed-assembly') {
    modelResult.root.name = 'imported-assembly'
  }
  const tabModelResultsByNodeId = createPuppyTabModelResultsByNodeId({
    modelResult,
    tabTemplates,
    topologySource,
  })
  const activateRuntimeMs = elapsedMs(activateStartMs)

  return {
    manifest: parsed.manifest,
    brepIndex: parsed.brepIndex,
    displayRoot: gltf.scene,
    assemblyDocument: assembly,
    projectModelDocument: parsed.projectModel,
    mates: assembly.mates,
    datumExtension,
    //------ 线性零部件阵列离线包定义持久化 开始------
    linearComponentPatternExtension,
    //------ 线性零部件阵列离线包定义持久化 结束------
    //------ 草图离线包定义持久化 开始------
    sketchExtension,
    sketchEntityReferenceExtension,
    //------ 草图离线包定义持久化 结束------
    //------ 特征离线包定义持久化 开始------
    featureExtension,
    //------ 特征离线包定义持久化 结束------
    sectionViewExtension,
    modelResult,
    tabTemplates,
    tabModelResultsByNodeId,
    topologySource,
    registeredBrepShapes,
    timing: createImportTiming({
      totalStartMs,
      parsePackageMs,
      parseDisplayGlbMs,
      registerBrepShapesMs,
      buildTopologySourceMs,
      activateRuntimeMs,
    }),
  }
}
